import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ReactComponent as DeleteIcon } from '~/assets/images/delete.svg';
import CardPagination from '~/components/CardPagination';
import Copy from '~/components/Copy';
import DataList from '~/components/DataView/DataList';
import DataTableCard from '~/components/DataView/DataTable';
import type { DataTableColumn } from '~/components/DataView/DataTable/utils/types';
import useResponsive from '~/hooks/useResponsive';
import ResponsiveActionCard from '~/Nova/scenes/Site/components/ResponsiveActionCard';

import { API_KEYS_ROWS_PER_PAGE } from '../../utils/constants';
import { AiApiKey, ApiKeysListViewProps } from '../../utils/types';
import ModalHeader from './ModalHeader';

const ApiKeysListView = ({
  gatewayEndpoint,
  keys,
  onCreate,
  onDelete,
  onClose,
  open = true,
}: ApiKeysListViewProps) => {
  const { t } = useTranslation('aiTooling', { keyPrefix: 'apiKeys.modal' });
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(API_KEYS_ROWS_PER_PAGE);

  useEffect(() => {
    if (!open) {
      setPage(0);
      setRowsPerPage(API_KEYS_ROWS_PER_PAGE);
    }
  }, [open]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(keys.length / rowsPerPage) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [keys.length, page, rowsPerPage]);

  const paginatedKeys = useMemo(() => {
    const startIndex = page * rowsPerPage;

    return keys.slice(startIndex, startIndex + rowsPerPage);
  }, [keys, page, rowsPerPage]);

  const handleChangePage = (
    _event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatLastUsed = (lastUsed: string) => lastUsed || t('table.never');

  const columns: DataTableColumn<AiApiKey>[] = [
    { id: 'name', label: t('table.name') },
    { id: 'created', label: t('table.created') },
    {
      id: 'lastUsed',
      label: t('table.lastUsed'),
      render: (apiKey) => formatLastUsed(apiKey.lastUsed),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (apiKey) => (
        <IconButton
          size="small"
          aria-label={t('deleteKey')}
          onClick={() => onDelete(apiKey)}
          data-testid="delete-api-key-btn"
        >
          <DeleteIcon width={15} height={16} aria-hidden />
        </IconButton>
      ),
    },
  ];

  const emptyState = (
    <Stack
      spacing={0.5}
      alignItems="center"
      textAlign="center"
      data-testid="manage-api-keys-empty"
    >
      <Typography variant="body2" fontWeight={600}>
        {t('empty.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('empty.description')}
      </Typography>
    </Stack>
  );

  const gatewayCard = (
    <ResponsiveActionCard
      title={t('gateway.title')}
      dataTestId="ai-gateway-card"
      sx={{ border: 'none' }}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('gateway.description')}
        </Typography>
        <Stack
          direction={isMobileView ? 'column' : 'row'}
          spacing={1}
          alignItems="center"
        >
          <TextField
            fullWidth
            size="small"
            value={gatewayEndpoint}
            InputProps={{
              readOnly: true,
              sx: {
                '& .MuiInputBase-input[readonly]': {
                  backgroundColor: 'background.disabled',
                },
              },
            }}
            data-testid="manage-api-keys-gateway-endpoint"
          />
          <Copy
            copyValue={gatewayEndpoint}
            variant="outlined"
            color="primary"
            fullWidth={isMobileView}
            dataTestId="manage-api-keys-copy-endpoint-btn"
            stackProps={{
              sx: { flexShrink: 0, width: isMobileView ? '100%' : 'auto' },
            }}
          />
        </Stack>
      </Stack>
    </ResponsiveActionCard>
  );

  const tablePagination = {
    resultCount: keys.length,
    page,
    rowsPerPage,
    onChangePage: handleChangePage,
    onChangeRowsPerPage: handleChangeRowsPerPage,
    sx: {
      bgcolor: 'common.white',
    },
  };

  const keysTable = (
    <DataTableCard
      columns={columns}
      rows={paginatedKeys}
      getRowId={(apiKey) => apiKey.id}
      emptyState={emptyState}
      cardPagination={tablePagination}
      data-testid="manage-api-keys-table"
      sx={{
        border: 'none',
        p: 0,
        '& .MuiTableCell-root:has([data-testid="manage-api-keys-empty"])': {
          py: 2,
        },
      }}
    />
  );

  // On mobile the columns don't fit, so render each key as a stacked card.
  const keysMobileList =
    keys.length > 0 ? (
      <Stack spacing={2} data-testid="manage-api-keys-list">
        {paginatedKeys.map((apiKey) => {
          const { id, name, created, lastUsed } = apiKey;
          return (
            <DataList
              key={id}
              rows={[
                { label: t('table.name'), value: name },
                { label: t('table.created'), value: created },
                { label: t('table.lastUsed'), value: formatLastUsed(lastUsed) },
              ]}
              actionButtons={
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DeleteIcon width={15} height={16} aria-hidden />}
                  onClick={() => onDelete(apiKey)}
                  data-testid="delete-api-key-btn"
                >
                  {t('deleteKey')}
                </Button>
              }
              sx={{ pt: 0 }}
            />
          );
        })}
      </Stack>
    ) : (
      emptyState
    );

  return (
    <>
      <ModalHeader
        title={t('title')}
        subtitle={t('subtitle')}
        onClose={onClose}
      />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {gatewayCard}

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreate}
              data-testid="create-api-key-btn"
              fullWidth={isMobileView}
            >
              {t('createKey')}
            </Button>
          </Stack>

          {isMobileView ? (
            <>
              {keysMobileList}
              {tablePagination && (
                <CardPagination {...tablePagination} isDataTable />
              )}
            </>
          ) : (
            keysTable
          )}
        </Stack>
      </DialogContent>
    </>
  );
};

export default ApiKeysListView;
